import { BinaryModel } from '@ctw/shared/api/fhir/models/binary';
import { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import { connectionOrEmpty, fqsRequest } from '@ctw/shared/api/fqs/client';
import type { InferQueryResult } from '@ctw/shared/api/fqs/helpers';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import { PatientRecordSearchDocument } from '@ctw/shared/types/graphql/__generated__/graphql';
import { QUERY_KEY_RESOURCE_SEARCH } from '@ctw/shared/utils/query-keys';
import type { UseQueryResult } from '@tanstack/react-query';
import type { Binary, DiagnosticReport } from 'fhir/r4';
import { compact } from 'lodash-es';

export type PatientRecordSearchQueryResult = InferQueryResult<typeof PatientRecordSearchDocument>;

export type PatientRecordSearchQuerySearchResultNode = NonNullable<
  PatientRecordSearchQueryResult['SearchConnection']
>['edges'][number]['node'];
export type PatientRecordSearchQueryMatchResult =
  PatientRecordSearchQuerySearchResultNode['matches'][number];

export interface PatientRecordSearchResult
  extends Omit<PatientRecordSearchQuerySearchResultNode, 'resource'> {
  allMatches: Array<PatientRecordSearchQueryMatchResult>;
  resource: BinaryModel | DiagnosticReportModel;
}

export interface PatientRecordSearchBinaryResult
  extends Omit<PatientRecordSearchQuerySearchResultNode, 'resource'> {
  allMatches: Array<PatientRecordSearchQueryMatchResult>;
  resource: BinaryModel;
}

export const isPatientRecordSearchBinaryResult = (
  result: PatientRecordSearchResult,
): result is PatientRecordSearchBinaryResult => result.resource instanceof BinaryModel;

// The list of resource types the FQS ZAP search API supports.
export const PATIENT_RECORD_SEARCH_RESOURCE_TYPES = ['Binary', 'DiagnosticReport'] as const;
export type PatientRecordSearchResourceType = (typeof PATIENT_RECORD_SEARCH_RESOURCE_TYPES)[number];

export interface PatientRecordSearchOption {
  label: string;
  resourceTypes: ReadonlyArray<PatientRecordSearchResourceType>;
}

// The list of resource type serach options we offer the user.
export const PATIENT_RECORD_SEARCH_OPTIONS = {
  all: { label: 'All Records', resourceTypes: PATIENT_RECORD_SEARCH_RESOURCE_TYPES },
  binary: { label: 'Documents', resourceTypes: ['Binary'] },
  diagnosticReport: { label: 'Diagnostic Reports', resourceTypes: ['DiagnosticReport'] },
} as const satisfies Record<string, PatientRecordSearchOption>;

export type PatientRecordSearchOptionValue = keyof typeof PATIENT_RECORD_SEARCH_OPTIONS;

export function usePatientRecordSearch(
  query: string,
  isExactMatch: boolean | undefined,
  resourceTypes: ReadonlyArray<PatientRecordSearchResourceType>,
): UseQueryResult<Array<PatientRecordSearchResult>> {
  const searchQueryParam = isExactMatch && query ? `"${query}"` : query;
  // Off, the request matches pre-flag behavior exactly: no `resourceTypes` variable at all.
  const hasDiagnosticReportSearchFeature = useFeatureFlag('fqs-zap-search-diagnostic-reports');

  return usePatientQuery({
    queryId: QUERY_KEY_RESOURCE_SEARCH,
    queryKey: [query, isExactMatch, resourceTypes],
    queryFn: async ({ telemetry, graphqlClient, patient }) => {
      const { data } = await fqsRequest<PatientRecordSearchQueryResult>(
        telemetry,
        graphqlClient,
        PatientRecordSearchDocument.toString(),
        {
          upid: patient.UPID,
          query: searchQueryParam,
          ...(hasDiagnosticReportSearchFeature && { resourceTypes }),
        },
      );

      return compact(
        connectionOrEmpty(data.SearchConnection).edges.map(
          (edge): PatientRecordSearchResult | undefined => {
            const allMatches: Array<PatientRecordSearchQueryMatchResult> = [...edge.node.matches];

            let resource: PatientRecordSearchResult['resource'] | undefined;
            switch (edge.node.resourceType) {
              case 'Binary':
                resource = new BinaryModel(edge.node.resource as unknown as Binary);
                break;
              case 'DiagnosticReport':
                resource = new DiagnosticReportModel(
                  edge.node.resource as unknown as DiagnosticReport,
                );
                break;
              default:
                // `result.node.resource` is a `GroupedSearchNode` which we can't handle unless we know
                // the type, so ignore it and filter it out
                resource = undefined;
                break;
            }

            return resource === undefined
              ? undefined
              : {
                  ...edge.node,
                  resource,
                  allMatches,
                };
          },
        ),
      );
    },
    enabled: !!query,
  });
}
